import { ApolloError, isApolloError } from '@apollo/client'
import { GraphQLError } from 'graphql'
import { ReactNode } from 'react'
import { FieldError } from 'react-hook-form'

import { CartFragment } from '../../graphql/magento'
import { rebateWithAutoShipOrDelayedBillingRemainder } from './config'

export const isCartWithAutoShip = (cart: CartFragment): boolean => {
  return cart.items != null
    ? !!cart.items.find((item) => {
        return item?.item_attributes?.dfh_order_type === 2
      })
    : false
}

export const isCartWithRebate = (cart: CartFragment): boolean => {
  return cart.rebate?.value != null && cart.rebate.value > 0
}

export const getEligibleRebateAmount = ({
  cart,
  currentBalance,
  delayedBilling,
}: {
  cart: CartFragment
  currentBalance: number
  delayedBilling: boolean
}): number => {
  const cartRebateValue = cart.rebate?.value || 0
  const cartGrandTotalValue = cart.prices?.grand_total?.value || 0
  const orderTotal = cartRebateValue + cartGrandTotalValue
  return Math.min(
    isCartWithAutoShip(cart) || delayedBilling
      ? orderTotal - rebateWithAutoShipOrDelayedBillingRemainder
      : orderTotal,
    currentBalance,
  )
}

export const areCartsEqual = (cart1?: CartFragment, cart2?: CartFragment): boolean => {
  return (
    !!cart1 &&
    !!cart2 &&
    (Object.is(cart1, cart2) || JSON.stringify(cart1) === JSON.stringify(cart2))
  )
}

export const isCartNotFoundError = (
  error: ApolloError | Error | undefined,
  cartId?: string,
): boolean => {
  if (!error) {
    return false
  }
  if (!isApolloError(error)) {
    return false
  }
  return isCartNotFoundGraphQlErrors(error.graphQLErrors, cartId)
}

export const isCartNotFoundGraphQlErrors = (
  graphQLErrors?: readonly GraphQLError[],
  cartId?: string,
): boolean => {
  if (!graphQLErrors || graphQLErrors.length === 0) {
    return false
  }
  return graphQLErrors.some(({ message, extensions, path }): boolean => {
    if (cartId && message.includes(cartId)) {
      return true
    }
    if (
      message.includes('Could not find a cart with ID') ||
      message.includes("The cart isn't active")
    ) {
      return true
    }
    // Retain the two error checks below known from M2 2.3.x for backward compatibility
    if (extensions?.category !== 'graphql-no-such-entity') {
      return false
    }
    if (message.includes('Current user does not have an active cart')) {
      return true
    }
    return !!path && path.includes('cart')
  })
}

/**
 * Magento throws this from `GetCartForUser` when the quote's `customer_id` does not equal the
 * caller's, which covers a logged-in customer using a guest cart id left over from before login,
 * and a guest using a quote since claimed by a customer. It does not cover a cart consumed by an
 * order or an otherwise inactive one - those deactivate the quote, so `GetCartForUser` reports
 * `The cart isn't active.` first, which `isCartNotFoundError` matches.
 *
 * Recoverable by loading a fresh cart, unlike `isCartNotFoundError`, which intentionally excludes
 * this case. Note the same check runs for cart queries, not only mutations.
 *
 * The message is paired with the `graphql-authorization` category Magento raises it under, so an
 * unrelated error family that quotes the phrase cannot trigger a cart replacement.
 */
export const isCartOwnershipError = (error: ApolloError | Error | undefined): boolean =>
  !!error &&
  isApolloError(error) &&
  error.graphQLErrors.some(
    ({ message, extensions }) =>
      extensions?.category === 'graphql-authorization' &&
      /current user cannot perform operations on cart/i.test(message),
  )

export const cartCoupons = (
  cart: CartFragment,
): NonNullable<CartFragment['applied_coupons']> | null =>
  cart.applied_coupons?.length ? cart.applied_coupons : null

export const cartCoupon = (cart: CartFragment): string | null => {
  const [firstCoupon] = cartCoupons(cart) || []
  return firstCoupon ? firstCoupon.code : null
}

export const formFieldLabelText = ({
  error,
  label,
}: {
  error: FieldError | undefined
  label: string | ReactNode
}): string | ReactNode => (error && error.message ? error.message : label)
